import { useCommandManager } from '../state/commandStore';

/**
 * Revit keyboard shortcuts import
 *
 * Parses an Autodesk Revit `KeyboardShortcuts.xml` export (Options → User
 * Interface → Keyboard Shortcuts → Export) and applies each shortcut to the
 * equivalent app command, so muscle memory from Revit keeps working here.
 *
 * Revit file shape (namespace-free, one `<Shortcut>` per binding):
 * ```xml
 * <Shortcuts>
 *   <Shortcut String="WA">
 *     <CommandName>ID_OBJECTS_WALL</CommandName>
 *     <Paths>
 *       <Path>Architecture-Build-Wall:Wall</Path>
 *     </Paths>
 *   </Shortcut>
 * </Shortcuts>
 * ```
 * `CommandName` is a Revit PostableCommand id. `Paths` is the ribbon path of
 * the primary button and is only used as a label-matching fallback.
 */

/** One parsed `<Shortcut>` entry from a Revit KeyboardShortcuts.xml file. */
export interface RevitShortcutEntry {
    /** Normalized app-format shortcut string ("WA", "Ctrl+D", "F8"). */
    shortcut: string;
    /** Raw Revit PostableCommand id (e.g. "ID_OBJECTS_WALL"). */
    commandName: string;
    /** First ribbon path declared for the command, when present. */
    path?: string;
}

/** One shortcut successfully assigned to an app command. */
export interface RevitImportApplied {
    commandId: string;
    commandLabel: string;
    shortcut: string;
    revitCommandName: string;
    /** Effective shortcut of the command before the import (null if none). */
    replaced: string | null;
}

export type RevitImportSkipReason =
    /** Revit command has no equivalent app command (or none registered yet). */
    | 'unmapped-command'
    /** Shortcut already claimed by an earlier entry in the same file. */
    | 'duplicate-shortcut'
    /** Command already received a (different) shortcut from the same file. */
    | 'duplicate-command'
    /** Malformed entry: missing shortcut string or command name. */
    | 'unknown-command';

export interface RevitImportSkipped {
    shortcut: string;
    revitCommandName: string;
    reason: RevitImportSkipReason;
}

/** A command outside the import whose effective shortcut equals an imported one. */
export interface RevitImportCollision {
    commandId: string;
    commandLabel: string;
    shortcut: string;
}

export interface RevitImportSummary {
    /** Number of `<Shortcut>` entries parsed from the file. */
    total: number;
    applied: RevitImportApplied[];
    skipped: RevitImportSkipped[];
    collisions: RevitImportCollision[];
}

export class RevitShortcutsParseError extends Error {}

/**
 * Canonical spelling of named keys the app's shortcut matcher knows
 * ("Esc", "Delete", "Enter" — see KeyboardListener/commandStore).
 */
const NAMED_KEY_CANONICAL: Record<string, string> = {
    ESC: 'Esc',
    ESCAPE: 'Esc',
    DEL: 'Delete',
    DELETE: 'Delete',
    ENTER: 'Enter',
    RETURN: 'Enter',
};

const MODIFIER_CANONICAL: Record<string, string> = {
    CTRL: 'Ctrl',
    CONTROL: 'Ctrl',
    ALT: 'Alt',
    SHIFT: 'Shift',
    META: 'Meta',
    CMD: 'Meta',
    COMMAND: 'Meta',
    WIN: 'Meta',
    WINDOWS: 'Meta',
};

/**
 * Normalize a Revit shortcut string into the app's format. Modifier combos
 * become "Ctrl+Alt+Shift+Meta<key>" in that exact order (the order
 * KeyboardListener constructs when matching); plain sequences are uppercased.
 */
export function normalizeShortcutString(raw: string): string {
    const value = raw.trim();
    if (!value) return '';
    if (value.includes('+')) {
        const tokens = value.split('+').map((t) => t.trim()).filter(Boolean);
        const key = tokens.pop() ?? '';
        const modifiers = new Set(
            tokens.map((t) => MODIFIER_CANONICAL[t.toUpperCase()] ?? '').filter(Boolean)
        );
        const canonical = ['Ctrl', 'Alt', 'Shift', 'Meta'].filter((m) => modifiers.has(m));
        return [...canonical, key.toUpperCase()].join('+');
    }
    const upper = value.toUpperCase();
    return NAMED_KEY_CANONICAL[upper] ?? upper;
}

/**
 * Revit PostableCommand id → app command id candidates (first registered
 * candidate wins). Keys are uppercase Revit command names; several aliases
 * are listed where Revit versions spell the same command differently.
 */
const REVIT_COMMAND_MAP: Record<string, string[]> = {};
const mapRevit = (appId: string, ...names: string[]) => {
    for (const name of names) {
        const key = name.toUpperCase();
        if (!REVIT_COMMAND_MAP[key]) REVIT_COMMAND_MAP[key] = [];
        REVIT_COMMAND_MAP[key].push(appId);
    }
};

// --- File / application menu ---------------------------------------------
mapRevit('newProject', 'ID_NEW_PROJECT');
mapRevit('openProject', 'ID_OPEN_PROJECT');
mapRevit('saveProject', 'ID_SAVE');
mapRevit('saveProjectAs', 'ID_SAVE_AS');
mapRevit('saveAsTemplate', 'ID_SAVE_AS_TEMPLATE');
mapRevit('undo', 'ID_EDIT_UNDO');
mapRevit('redo', 'ID_EDIT_REDO');
mapRevit('openSettings', 'ID_OPTIONS', 'ID_APPLICATION_SETTINGS');
mapRevit('openShortcuts', 'ID_KEYBOARD_SHORTCUTS');
mapRevit('helpPanel', 'ID_HELP', 'ID_HELP_CONTENTS');
mapRevit('openFind', 'ID_FIND_REPLACE', 'ID_FIND');
mapRevit('browserOrganization', 'ID_BROWSER_ORGANIZATION');

// --- Datum, views & navigation --------------------------------------------
mapRevit('activateLevel', 'ID_OBJECTS_LEVEL', 'ID_VIEW_LEVEL');
mapRevit('activateGrid', 'ID_OBJECTS_GRID');
mapRevit('activatePlanView', 'ID_NEW_PLAN_VIEW', 'ID_NEW_FLOOR_PLAN', 'ID_VIEW_PLAN');
mapRevit('activate3DView', 'ID_VIEW_3D', 'ID_NEW_DEFAULT_3D_VIEW', 'ID_3D_VIEW');
mapRevit('activateViewSection', 'ID_VIEW_SECTION');
mapRevit('activateViewElevation', 'ID_VIEW_ELEVATION');
mapRevit('activateViewCallout', 'ID_VIEW_CALLOUT');
mapRevit('openNewSheet', 'ID_NEW_SHEET', 'ID_VIEW_NEW_SHEET');
mapRevit('zoomToFit', 'ID_VIEW_ZOOM_FIT', 'ID_VIEW_ZOOM_ALL');
mapRevit('zoomSheetSize', 'ID_VIEW_ZOOM_SHEET', 'ID_VIEW_ZOOM_SHEET_SIZE');
mapRevit('zoomInRegion', 'ID_VIEW_ZOOM_IN_REGION', 'ID_VIEW_ZOOM_REGION', 'ID_VIEW_ZOOM_IN');
mapRevit('zoomOut', 'ID_VIEW_ZOOM_OUT', 'ID_VIEW_ZOOM_OUT_2', 'ID_VIEW_ZOOM_OUT_2X');
mapRevit('zoomToSelection', 'ID_VIEW_ZOOM_TO_SELECTION', 'ID_VIEW_ZOOM_SELECTION');
mapRevit('executeVisibilityGraphics', 'ID_VISIBILITY_GRAPHICS');
mapRevit('viewProperties', 'ID_VIEW_PROPERTIES');
mapRevit('showProperties', 'ID_PROPERTIES', 'ID_PROPERTIES_PALETTE');
mapRevit('editType', 'ID_TYPE_PROPERTIES', 'ID_FAMILY_TYPE');
mapRevit('executeThinLines', 'ID_THIN_LINES');
mapRevit('executeProjectUnits', 'ID_PROJECT_UNITS');

// --- Architecture / structure build tools ---------------------------------
mapRevit('activateWall', 'ID_OBJECTS_WALL');
mapRevit('activateDoor', 'ID_OBJECTS_DOOR');
mapRevit('activateWindow', 'ID_OBJECTS_WINDOW');
mapRevit('activateComponent', 'ID_OBJECTS_COMPONENT');
mapRevit('activateModelInPlace', 'ID_OBJECTS_MODEL_IN_PLACE', 'ID_OBJECTS_IN_PLACE_COMPONENT');
mapRevit('activateFloor', 'ID_OBJECTS_FLOOR');
mapRevit('activateRoof', 'ID_OBJECTS_ROOF');
mapRevit('activateCeiling', 'ID_OBJECTS_CEILING');
mapRevit('activateStair', 'ID_OBJECTS_STAIR', 'ID_STAIRS_STAIR');
mapRevit('activateRailing', 'ID_OBJECTS_RAILING');
mapRevit('activateRamp', 'ID_OBJECTS_RAMP');
mapRevit('activateCurtainWall', 'ID_OBJECTS_CURTAIN_WALL');
mapRevit('activateCurtainPanelByFace', 'ID_OBJECTS_CURTAIN_SYSTEM_BY_FACE');
mapRevit('activateColumn', 'ID_OBJECTS_COLUMN', 'ID_STRUCTURAL_COLUMN');
mapRevit('activateBeam', 'ID_OBJECTS_BEAM', 'ID_STRUCTURAL_BEAM');
mapRevit('activateBrace', 'ID_OBJECTS_BRACE', 'ID_STRUCTURAL_BRACE');
mapRevit('activateBeamSystem', 'ID_OBJECTS_BEAM_SYSTEM', 'ID_STRUCTURAL_BEAM_SYSTEM');
mapRevit('activateTruss', 'ID_OBJECTS_TRUSS');
mapRevit(
    'activateIsolatedFoundation',
    'ID_OBJECTS_ISOLATED_FOUNDATION',
    'ID_STRUCTURAL_FOUNDATION',
    'ID_OBJECTS_FOUNDATION'
);
mapRevit('activateWallFoundation', 'ID_OBJECTS_WALL_FOUNDATION');
mapRevit('activateSlabFoundation', 'ID_OBJECTS_SLAB', 'ID_OBJECTS_SLAB_FOUNDATION');
mapRevit('activateStructuralWall', 'ID_STRUCTURAL_WALL');
mapRevit('activateStructuralFloor', 'ID_STRUCTURAL_FLOOR', 'ID_OBJECTS_STRUCTURAL_FLOOR');
mapRevit('activateRebar', 'ID_OBJECTS_REBAR');
mapRevit('activateAreaReinforcement', 'ID_OBJECTS_AREA_REINFORCEMENT');
mapRevit('activatePathReinforcement', 'ID_OBJECTS_PATH_REINFORCEMENT');
mapRevit('activateFabricReinforcement', 'ID_OBJECTS_FABRIC_REINFORCEMENT');
mapRevit('activateRebarCover', 'ID_OBJECTS_REBAR_COVER', 'ID_REBAR_COVER');
mapRevit('activateOpeningByFace', 'ID_OBJECTS_OPENING_BY_FACE');
mapRevit('activateOpeningShaft', 'ID_OBJECTS_SHAFT_OPENING', 'ID_OBJECTS_SHAFT');
mapRevit('activateOpeningWall', 'ID_OBJECTS_WALL_OPENING');
mapRevit('activateOpeningVertical', 'ID_OBJECTS_VERTICAL_OPENING');
mapRevit('activateOpeningCut', 'ID_OBJECTS_CUT_OPENING');
mapRevit('activateModelText', 'ID_OBJECTS_MODEL_TEXT');
mapRevit('activateRoom', 'ID_OBJECTS_ROOM');
mapRevit('activateArea', 'ID_OBJECTS_AREA');
mapRevit('activateSpace', 'ID_OBJECTS_SPACE');
mapRevit('activateZone', 'ID_OBJECTS_ZONE');
mapRevit('activateSpaceSeparator', 'ID_OBJECTS_SPACE_SEPARATOR');
mapRevit('activateToposolid', 'ID_OBJECTS_TOPOSOLID', 'ID_OBJECTS_TOPO_SURFACE');
mapRevit('activateBuildingPad', 'ID_OBJECTS_BUILDING_PAD');
mapRevit('activatePropertyLine', 'ID_OBJECTS_PROPERTY_LINE');
mapRevit('activateSubRegion', 'ID_OBJECTS_SUBREGION');
mapRevit('activateGradedRegion', 'ID_OBJECTS_GRADED_REGION');
mapRevit('activateSiteComponent', 'ID_OBJECTS_SITE_COMPONENT');
mapRevit('activateParkingComponent', 'ID_OBJECTS_PARKING', 'ID_OBJECTS_PARKING_COMPONENT');
mapRevit('activatePlaceMass', 'ID_OBJECTS_MASS', 'ID_OBJECTS_PLACE_MASS');
mapRevit('activateMassingInPlace', 'ID_OBJECTS_IN_PLACE_MASS', 'ID_MASS_IN_PLACE');
mapRevit('activateWallByFaceMassing', 'ID_OBJECTS_WALL_BY_FACE');
mapRevit('activateFloorByFaceMassing', 'ID_OBJECTS_FLOOR_BY_FACE');
mapRevit('activateRoofByFaceMassing', 'ID_OBJECTS_ROOF_BY_FACE');
mapRevit('activateDecal', 'ID_OBJECTS_DECAL');

// --- MEP -------------------------------------------------------------------
mapRevit('activateDuct', 'ID_OBJECTS_DUCT');
mapRevit('activateDuctPlaceholder', 'ID_OBJECTS_DUCT_PLACEHOLDER');
mapRevit('activateFlexDuct', 'ID_OBJECTS_FLEX_DUCT');
mapRevit('activateDuctFitting', 'ID_OBJECTS_DUCT_FITTING');
mapRevit('activateDuctAccessory', 'ID_OBJECTS_DUCT_ACCESSORY');
mapRevit('activateAirTerminal', 'ID_OBJECTS_AIR_TERMINAL');
mapRevit('activateMechanicalEquipment', 'ID_OBJECTS_MECHANICAL_EQUIPMENT');
mapRevit('activatePipe', 'ID_OBJECTS_PIPE');
mapRevit('activatePipePlaceholder', 'ID_OBJECTS_PIPE_PLACEHOLDER');
mapRevit('activateFlexPipe', 'ID_OBJECTS_FLEX_PIPE');
mapRevit('activatePipeFitting', 'ID_OBJECTS_PIPE_FITTING');
mapRevit('activatePipeAccessory', 'ID_OBJECTS_PIPE_ACCESSORY');
mapRevit('activatePlumbingFixture', 'ID_OBJECTS_PLUMBING_FIXTURE');
mapRevit('activateSprinkler', 'ID_OBJECTS_SPRINKLER');
mapRevit('activateCableTray', 'ID_OBJECTS_CABLE_TRAY');
mapRevit('activateCableTrayFitting', 'ID_OBJECTS_CABLE_TRAY_FITTING', 'ID_OBJECTS_CONDUIT_FITTING');
mapRevit('activateConduit', 'ID_OBJECTS_CONDUIT');
mapRevit('activateWire', 'ID_OBJECTS_WIRE');
mapRevit('activateElectricalEquipment', 'ID_OBJECTS_ELECTRICAL_EQUIPMENT');
mapRevit('activateLightingFixture', 'ID_OBJECTS_LIGHTING_FIXTURE');
mapRevit(
    'activateDevice',
    'ID_OBJECTS_LIGHTING_DEVICE',
    'ID_OBJECTS_ELECTRICAL_DEVICE',
    'ID_OBJECTS_DEVICE'
);
mapRevit('activateSwitchSystem', 'ID_OBJECTS_SWITCH_SYSTEM');
mapRevit('activateFabricationPart', 'ID_OBJECTS_FABRICATION_PART');

// --- Annotate ---------------------------------------------------------------
mapRevit('activateDimension', 'ID_OBJECTS_DIMENSION', 'ID_OBJECTS_DIMENSION_ALIGNED');
mapRevit('activateLinearDimension', 'ID_OBJECTS_LINEAR_DIMENSION');
mapRevit('activateAngularDimension', 'ID_OBJECTS_ANGULAR_DIMENSION');
mapRevit('activateRadialDimension', 'ID_OBJECTS_RADIAL_DIMENSION');
mapRevit('activateDiameterDimension', 'ID_OBJECTS_DIAMETER_DIMENSION');
mapRevit('activateArcLengthDimension', 'ID_OBJECTS_ARC_LENGTH_DIMENSION');
mapRevit('activateSpotElevation', 'ID_OBJECTS_SPOT_ELEVATION');
mapRevit('activateSpotCoordinate', 'ID_OBJECTS_SPOT_COORDINATE');
mapRevit('activateSpotSlope', 'ID_OBJECTS_SPOT_SLOPE');
mapRevit('activateText', 'ID_OBJECTS_TEXTNOTE');
mapRevit('activateTag', 'ID_OBJECTS_TAG');
mapRevit('activateTagAll', 'ID_OBJECTS_TAG_ALL', 'ID_TAG_ALL', 'ID_TAG_ALL_NOT_TAGGED');
mapRevit('activateTagRoom', 'ID_OBJECTS_ROOM_TAG');
mapRevit('activateTagArea', 'ID_OBJECTS_AREA_TAG');
mapRevit('activateTagSpace', 'ID_OBJECTS_SPACE_TAG');
mapRevit('activateTagMaterial', 'ID_OBJECTS_MATERIAL_TAG');
mapRevit('activateTagMultiCategory', 'ID_OBJECTS_MULTI_CATEGORY_TAG');
mapRevit('activateKeynote', 'ID_OBJECTS_KEYNOTE', 'ID_OBJECTS_KEY_NOTE');
mapRevit('activateKeynoteElement', 'ID_OBJECTS_ELEMENT_KEYNOTE');
mapRevit('activateKeynoteMaterial', 'ID_OBJECTS_MATERIAL_KEYNOTE');
mapRevit('activateKeynoteUser', 'ID_OBJECTS_USER_KEYNOTE');
mapRevit('activateSymbol', 'ID_OBJECTS_SYMBOL');
mapRevit('activateDetailLine', 'ID_OBJECTS_DETAIL_LINE');
mapRevit('activateDetailComponent', 'ID_OBJECTS_DETAIL_COMPONENT');
mapRevit('activateRepeatingDetail', 'ID_OBJECTS_REPEATING_DETAIL');
mapRevit('activateFilledRegion', 'ID_OBJECTS_FILLED_REGION');
mapRevit('activateMaskingRegion', 'ID_OBJECTS_MASKING_REGION');
mapRevit('activateInsulation', 'ID_OBJECTS_INSULATION');
mapRevit('activateRevisionCloud', 'ID_OBJECTS_REVISION_CLOUD');
mapRevit('activateColorFillLegend', 'ID_OBJECTS_COLOR_FILL_LEGEND');
mapRevit('activateMultiRebarTag', 'ID_OBJECTS_MULTI_REBAR_TAG');
mapRevit('activateLinework', 'ID_OBJECTS_LINEWORK');
mapRevit('activateDemolition', 'ID_OBJECTS_DEMOLITION', 'ID_DEMOLISH');

// --- Modify -----------------------------------------------------------------
mapRevit('cancel', 'ID_EDIT_MODIFY', 'ID_MODIFY');
mapRevit('delete', 'ID_EDIT_DELETE', 'ID_DELETE');
mapRevit('move', 'ID_EDIT_MOVE');
mapRevit('copy', 'ID_EDIT_COPY');
mapRevit('paste', 'ID_EDIT_PASTE');
mapRevit('cut', 'ID_EDIT_CUT');
mapRevit('rotate', 'ID_EDIT_ROTATE');
mapRevit('array', 'ID_EDIT_ARRAY');
mapRevit('scale', 'ID_EDIT_SCALE');
mapRevit('align', 'ID_EDIT_ALIGN');
mapRevit('setOffset', 'ID_EDIT_OFFSET');
mapRevit('mirrorPickAxis', 'ID_EDIT_MIRROR_PICK_AXIS');
mapRevit('mirrorDrawAxis', 'ID_EDIT_MIRROR_DRAW_AXIS');
mapRevit('split', 'ID_EDIT_SPLIT');
mapRevit('trim', 'ID_EDIT_TRIM_EXTEND');
mapRevit('createSimilar', 'ID_EDIT_CREATE_SIMILAR');
mapRevit('activateMatchType', 'ID_MODIFY_MATCH_TYPE');
mapRevit('pin', 'ID_EDIT_PIN', 'ID_PIN');
mapRevit('unpin', 'ID_EDIT_UNPIN', 'ID_UNPIN');
mapRevit('createGroup', 'ID_EDIT_GROUP', 'ID_MODEL_GROUP');
mapRevit('selectAll', 'ID_SELECT_ALL');
mapRevit('filterSelection', 'ID_FILTER', 'ID_FILTER_SELECTION');
mapRevit('repeatLastCommand', 'ID_REPEAT_LAST_COMMAND');
mapRevit('flipFacing', 'ID_FLIP_FACING', 'ID_FLOORPLAN_FLIP_FACING');
mapRevit('flipHand', 'ID_FLIP_HAND', 'ID_FLOORPLAN_FLIP_HAND');
mapRevit('flipOrientation', 'ID_FLIP_ORIENTATION');
mapRevit('activateJoin', 'ID_JOIN_GEOMETRY');
mapRevit('activateUnjoin', 'ID_UNJOIN_GEOMETRY');
mapRevit('activateCutGeometry', 'ID_CUT_GEOMETRY');
mapRevit('activateUncutGeometry', 'ID_UNCUT_GEOMETRY');
mapRevit('pasteAlignedSamePlace', 'ID_PASTE_ALIGNED_SAME_PLACE', 'ID_EDIT_PASTE_ALIGNED_CURRENT_VIEW');
mapRevit(
    'pasteAlignedSelectedLevels',
    'ID_PASTE_ALIGNED_SELECTED_LEVELS',
    'ID_EDIT_PASTE_ALIGNED_TO_SELECTED_LEVELS'
);
mapRevit(
    'pasteAlignedSelectedViews',
    'ID_PASTE_ALIGNED_SELECTED_VIEWS',
    'ID_EDIT_PASTE_ALIGNED_TO_SELECTED_VIEWS'
);

// --- Collaborate & links ------------------------------------------------------
mapRevit('executeSynchronize', 'ID_SYNCHRONIZE', 'ID_SYNCHRONIZE_WITH_CENTRAL');
mapRevit('executeReloadLatest', 'ID_RELOAD_LATEST');
mapRevit('openWorksetsDialog', 'ID_WORKSETS');
mapRevit('openCopyMonitorDialog', 'ID_COPY_MONITOR');
mapRevit('openCoordinationReviewDialog', 'ID_COORDINATION_REVIEW');
mapRevit(
    'openInterferenceCheckDialog',
    'ID_INTERFERENCE_CHECK',
    'ID_CHECK_INTERFERENCE',
    'ID_RUN_INTERFERENCE_CHECK'
);
mapRevit('openRelinquishDialog', 'ID_RELINQUISH_ALL_MINE');
mapRevit('executeLoadFamily', 'ID_LOAD_FAMILY');
mapRevit('activateLoadGroup', 'ID_LOAD_AS_GROUP');
mapRevit('activateManageLinks', 'ID_MANAGE_LINKS');
mapRevit('activateManageImages', 'ID_MANAGE_IMAGES');
mapRevit('activateLinkCAD', 'ID_LINK_CAD');
mapRevit('activateImportCAD', 'ID_IMPORT_CAD');
mapRevit('activateLinkIFC', 'ID_LINK_IFC');
mapRevit('activateImportPDF', 'ID_IMPORT_PDF');
mapRevit('activateLinkPDFImage', 'ID_LINK_IMAGE', 'ID_IMPORT_IMAGE', 'ID_LINK_PDF');
mapRevit('activateLinkPointCloud', 'ID_LINK_POINT_CLOUD');
mapRevit('activateLinkCoordinationModel', 'ID_LINK_COORDINATION_MODEL');
mapRevit('activateLinkTopography', 'ID_LINK_TOPOGRAPHY');
mapRevit('editFamily', 'ID_EDIT_FAMILY');
mapRevit('executeExportGbXML', 'ID_EXPORT_GBXML');
mapRevit('executeCreateEnergyModel', 'ID_CREATE_ENERGY_MODEL');
mapRevit('executePerformEnergy', 'ID_RUN_ENERGY_ANALYSIS', 'ID_PERFORM_ENERGY_ANALYSIS');
mapRevit('executeHeatingCoolingLoads', 'ID_HEATING_COOLING_LOADS', 'ID_RUN_HEATING_COOLING_LOADS');

// --- Temporary hide/isolate ---------------------------------------------------
mapRevit('hideElements', 'ID_HIDE_OBJECT', 'ID_TEMP_HIDE_OBJECT', 'ID_HIDE_ELEMENT');
mapRevit('isolateElement', 'ID_ISOLATE_OBJECT', 'ID_TEMP_ISOLATE_OBJECT', 'ID_ISOLATE_ELEMENT');
mapRevit('hideCategory', 'ID_HIDE_CATEGORY', 'ID_TEMP_HIDE_CATEGORY');
mapRevit('isolateCategory', 'ID_ISOLATE_CATEGORY', 'ID_TEMP_ISOLATE_CATEGORY');
mapRevit(
    'resetTemporaryHideIsolate',
    'ID_RESET_TEMPORARY_HIDE_ISOLATE',
    'ID_RESET_TEMP_HIDE_ISOLATE',
    'ID_TEMP_RESET'
);
mapRevit('hideByFilter', 'ID_HIDE_BY_FILTER');

/**
 * Parse a Revit KeyboardShortcuts.xml export into shortcut entries.
 * Throws {@link RevitShortcutsParseError} for empty or non-XML input.
 */
export function parseRevitShortcutsXml(xml: string): RevitShortcutEntry[] {
    if (!xml || !xml.trim()) {
        throw new RevitShortcutsParseError('The file is empty.');
    }
    // Namespace-tolerant: Revit exports are namespace-free, but strip xmlns
    // declarations so a namespaced variant still resolves by plain tag name.
    const sanitized = xml.replace(/\sxmlns(:\w+)?="[^"]*"/g, '');
    const doc = new DOMParser().parseFromString(sanitized, 'application/xml');
    if (doc.getElementsByTagName('parsererror').length > 0) {
        throw new RevitShortcutsParseError('The file is not valid XML.');
    }

    // Canonical Revit schema uses <Shortcut String="WA">; older/tooling
    // variants expose the same data as <ShortcutItem> with child elements.
    const nodes = [
        ...Array.from(doc.getElementsByTagName('Shortcut')),
        ...Array.from(doc.getElementsByTagName('ShortcutItem')),
    ];

    const childText = (parent: Element, tag: string) =>
        parent.getElementsByTagName(tag)[0]?.textContent?.trim() ?? '';

    const entries: RevitShortcutEntry[] = [];
    for (const node of nodes) {
        const rawShortcut =
            node.getAttribute('String') ??
            node.getAttribute('ShortcutString') ??
            childText(node, 'ShortcutString');
        const shortcut = normalizeShortcutString(rawShortcut);
        if (!shortcut) continue; // Binding without keys can never be applied.
        const commandName =
            node.getAttribute('CommandName') ?? childText(node, 'CommandName') ?? '';
        const pathNode = node.getElementsByTagName('Path')[0];
        const path =
            pathNode?.textContent?.trim() ||
            pathNode?.getAttribute('PathName') ||
            node.getAttribute('Path') ||
            undefined;
        entries.push({ shortcut, commandName, path: path || undefined });
    }
    return entries;
}

/** Resolve the app command an entry should bind to, or undefined if none. */
function resolveAppCommandId(
    entry: RevitShortcutEntry,
    commands: Record<string, { id: string; label: string }>,
    alreadyAssigned: ReadonlyMap<string, string>
): string | undefined {
    // 1) Exact PostableCommand mapping (version-tolerant alias list).
    const candidates = REVIT_COMMAND_MAP[entry.commandName.trim().toUpperCase()];
    if (candidates) {
        for (const id of candidates) {
            if (commands[id] && !alreadyAssigned.has(id)) return id;
        }
    }
    // 2) Fallback: the ribbon path's button name ("...-Build-Wall:Wall" →
    // "Wall") exactly equals a command label. Contextual paths without a
    // ":Button" segment ("Modify | Place Wall") never match.
    if (entry.path && entry.path.includes(':')) {
        const label = entry.path.split(':').pop()?.trim().toLowerCase();
        if (label) {
            for (const cmd of Object.values(commands)) {
                if (cmd.label.toLowerCase() === label && !alreadyAssigned.has(cmd.id)) {
                    return cmd.id;
                }
            }
        }
    }
    return undefined;
}

/**
 * Import a Revit KeyboardShortcuts.xml export: parse → resolve → apply as
 * user shortcuts on the command store (persisted by zustand persist).
 *
 * Rules: file order wins — the first entry for a shortcut (or for a command)
 * is applied and later duplicates are reported as skipped. Assignments are
 * still applied when they shadow a command outside the import; those pairs
 * are returned as `collisions` for the caller to surface.
 */
export function importRevitShortcuts(xml: string): RevitImportSummary {
    const entries = parseRevitShortcutsXml(xml);
    const { commands, updateShortcut } = useCommandManager.getState();

    const applied: RevitImportApplied[] = [];
    const skipped: RevitImportSkipped[] = [];
    const assigned = new Map<string, string>(); // commandId → shortcut
    const claimedShortcuts = new Set<string>(); // shortcuts applied in this run

    for (const entry of entries) {
        const base = { shortcut: entry.shortcut, revitCommandName: entry.commandName };
        if (!entry.commandName.trim()) {
            skipped.push({ ...base, reason: 'unknown-command' });
            continue;
        }
        if (claimedShortcuts.has(entry.shortcut)) {
            skipped.push({ ...base, reason: 'duplicate-shortcut' });
            continue;
        }
        const commandId = resolveAppCommandId(entry, commands, assigned);
        if (!commandId) {
            skipped.push({ ...base, reason: 'unmapped-command' });
            continue;
        }
        if (assigned.has(commandId)) {
            skipped.push({ ...base, reason: 'duplicate-command' });
            continue;
        }
        const command = commands[commandId];
        const replaced = (command.shortcut ?? command.defaultShortcut ?? '').trim() || null;
        updateShortcut(commandId, entry.shortcut);
        assigned.set(commandId, entry.shortcut);
        claimedShortcuts.add(entry.shortcut);
        applied.push({
            commandId,
            commandLabel: command.label,
            shortcut: entry.shortcut,
            revitCommandName: entry.commandName,
            replaced,
        });
    }

    // Effective-shortcut collisions with commands the import did not touch.
    const collisions: RevitImportCollision[] = [];
    const finalCommands = useCommandManager.getState().commands;
    for (const cmd of Object.values(finalCommands)) {
        if (assigned.has(cmd.id)) continue;
        const effective = (cmd.shortcut ?? cmd.defaultShortcut ?? '').trim().toLowerCase();
        if (!effective) continue;
        for (const [commandId, shortcut] of assigned) {
            if (shortcut.toLowerCase() === effective) {
                collisions.push({ commandId: cmd.id, commandLabel: cmd.label, shortcut });
                break;
            }
        }
    }

    return { total: entries.length, applied, skipped, collisions };
}
